import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ExternalLink } from 'lucide-react';
import { EmbedInfo, releaseEmbed } from '@/lib/embed';
import { ApiRelease } from '@/hooks/useSiteContent';
import { mediaUrl } from '@/lib/api';
import { useNotice } from '@/hooks/useNotice';
import ReleasePlayer, { playableTracks } from './ReleasePlayer';

interface EmbedReq {
  title: string;
  subtitle?: string;
  embed: EmbedInfo;
  link?: string;
}

interface Ctx {
  openEmbed: (r: EmbedReq) => void;
  openAll: () => void;
  /** opens the tracklist player (or the Spotify embed); false if the release has neither */
  openRelease: (r: ApiRelease, opts?: { force?: boolean }) => boolean;
}

export const canOpenRelease = (r: ApiRelease) => playableTracks(r).length > 0 || !!releaseEmbed(r);

const ModalCtx = createContext<Ctx>({ openEmbed: () => {}, openAll: () => {}, openRelease: () => false });
export const usePlayerModal = () => useContext(ModalCtx);

function Shell({ onClose, children, label, testId, wide }: { onClose: () => void; children: React.ReactNode; label: string; testId: string; wide?: boolean }) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    const ov = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = ov;
      prev?.focus?.();
    };
  }, [onClose]);
  return createPortal(
    <div className="ek-modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`ek-modal ${wide ? 'is-wide' : ''}`} role="dialog" aria-modal="true" aria-label={label} data-testid={testId}>
        <button ref={closeRef} type="button" className="ek-modal-close" onClick={onClose} aria-label="Close" data-testid="embed-player-close-button">
          <X size={18} />
        </button>
        {children}
      </div>
    </div>,
    document.body,
  );
}

export function PlayerModalProvider({ allReleases, children }: { allReleases: ApiRelease[]; children: React.ReactNode }) {
  const [embed, setEmbed] = useState<EmbedReq | null>(null);
  const [all, setAll] = useState(false);
  const [rel, setRel] = useState<ApiRelease | null>(null);
  const { notify } = useNotice();
  const openEmbed = useCallback((r: EmbedReq) => {
    setAll(false);
    setRel(null);
    setEmbed(r);
  }, []);
  const openRelease = useCallback(
    (r: ApiRelease, opts?: { force?: boolean }) => {
      // shared links (/release/<slug>) always open the release card, even without audio or an embed
      if (playableTracks(r).length || releaseEmbed(r) || opts?.force) {
        setAll(false);
        setEmbed(null);
        setRel(r);
        if (r.slug) window.history.replaceState(window.history.state, '', `/release/${r.slug}`);
        return true;
      }
      if (playableTracks(r).length) {
        setAll(false);
        setEmbed(null);
        setRel(r);
        return true;
      }
      const emb = releaseEmbed(r);
      if (emb) {
        openEmbed({ title: r.title, subtitle: [r.artist, r.type, r.year].filter(Boolean).join(' • '), embed: emb, link: r.spotify_url });
        return true;
      }
      return false;
    },
    [openEmbed],
  );
  const closeRel = useCallback(() => {
    setRel(null);
    if (window.location.pathname.startsWith('/release/')) window.history.replaceState(window.history.state, '', '/');
  }, []);
  const openAll = useCallback(() => setAll(true), []);
  const close = useCallback(() => setEmbed(null), []);
  const closeAll = useCallback(() => setAll(false), []);

  return (
    <ModalCtx.Provider value={{ openEmbed, openAll, openRelease }}>
      {children}
      {rel && (
        <Shell onClose={closeRel} label={`${rel.title} player`} testId="release-player-dialog">
          <ReleasePlayer release={rel} />
        </Shell>
      )}
      {embed && (
        <Shell onClose={close} label={`${embed.title} player`} testId="embed-player-dialog">
          <div className="ek-modal-head">
            <p className="ek-modal-kicker">NOW PLAYING</p>
            <h3 className="ek-modal-title">{embed.title}</h3>
            {embed.subtitle && <p className="ek-modal-sub">{embed.subtitle}</p>}
          </div>
          <iframe
            className="ek-modal-iframe"
            data-testid="embed-player-iframe"
            title={`${embed.title} — embedded player`}
            src={embed.embed.src}
            height={embed.embed.height}
            loading="lazy"
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
          />
          {embed.link && (
            <a className="ek-modal-open" href={embed.link} target="_blank" rel="noopener noreferrer" data-testid="embed-player-open-external">
              Open in {embed.embed.provider === 'spotify' ? 'Spotify' : 'player'} <ExternalLink size={13} />
            </a>
          )}
        </Shell>
      )}
      {all && (
        <Shell onClose={closeAll} label="All releases" testId="all-releases-dialog" wide>
          <div className="ek-modal-head">
            <h3 className="ek-modal-title ek-modal-title-caps">ALL RELEASES</h3>
          </div>
          <div className="ek-all-grid">
            {allReleases.map((r) => {
              return (
                <button
                  key={r.id}
                  type="button"
                  className="ek-all-card"
                  data-testid={`all-release-${r.slug || r.id}`}
                  onClick={() => {
                    if (openRelease(r)) return;
                    if (r.spotify_url) window.open(r.spotify_url, '_blank', 'noopener');
                    else notify(`${r.title} link is not configured yet.`);
                  }}
                >
                  <img src={mediaUrl(r.cover_url)} alt={`${r.title} cover art`} />
                  <span className="ek-all-name">{r.title}</span>
                  <span className="ek-all-meta">
                    {[r.type, r.year].filter(Boolean).join(' • ').toUpperCase()}
                  </span>
                </button>
              );
            })}
          </div>
        </Shell>
      )}
    </ModalCtx.Provider>
  );
}
